import {
  AppCard,
  Button,
  FormTextField,
  Icon,
  RouteLink,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiEmailOutline } from "@mdi/js";
import { Box, Container, Divider, Typography } from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import axios from "axios";
import { useSnackbar } from "notistack";
import { useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { EquinorLogo } from "../../../../../components/atoms/EquinorLogo/EquinorLogo";
import {
  Recaptcha,
  type RecaptchaRef,
} from "../../../../../components/atoms/Recaptcha/Recaptcha";
import { config } from "../../../../../config";
import {
  type ForgotPasswordFormData,
  forgotPasswordSchema,
} from "../-schemas/forgotPasswordSchema";

const RouteComponent = () => {
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef<RecaptchaRef>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, setValue } = useForm({
    defaultValues: {
      email: "",
    },
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit: SubmitHandler<ForgotPasswordFormData> = async (values) => {
    const token = recaptchaRef.current?.getValue();
    if (
      !token &&
      !(window.environment === "test" || window.environment === "development")
    ) {
      enqueueSnackbar(t("auth.signIn.invalidBotVerificationMessage"), {
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const bodyFormData = new FormData();
      bodyFormData.append("user[email]", values.email);
      bodyFormData.append("g-recaptcha-response", token ?? "");

      const response = await axios.postForm("/users/password", bodyFormData, {
        xsrfCookieName: config.csrfTokenCookieName,
        xsrfHeaderName: "x-csrf-token",
      });

      if (response.status === 200) {
        enqueueSnackbar(t("auth.forgotPassword.successMessage"), {
          variant: "success",
        });
        navigate({
          to: "/users/sign_in",
        });
      }
    } catch {
      enqueueSnackbar(t("common.genericError"), {
        variant: "error",
      });
    } finally {
      recaptchaRef.current?.reset();
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="xs"
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
        padding: "0px !important",
      }}
    >
      <AppCard slotProps={{ root: { sx: { padding: 3 } } }}>
        <EquinorLogo />
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Typography component="h1" sx={{ textAlign: "center" }} variant="h5">
            {t("auth.forgotPassword.title")}
          </Typography>
        </Box>
        <form
          name="forgotPasswordForm"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormTextField
              control={control}
              fullWidth
              margin="normal"
              name="email"
              onChange={(event) => {
                const normalizedValue = event.target.value.trim().toLowerCase();
                if (event.target.value !== normalizedValue) {
                  setValue("email", normalizedValue, {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                }
              }}
              placeholder={t("common.email")}
              required
              slotProps={{
                input: {
                  startAdornment: (
                    <Icon
                      icon={mdiEmailOutline}
                      sx={{
                        color: (currentTheme) =>
                          currentTheme.vars.palette.action.active,
                        marginRight: 1,
                      }}
                    />
                  ),
                },
              }}
              type="email"
            />

            <Recaptcha ref={recaptchaRef} />

            <Button
              fullWidth
              loading={loading}
              loadingPosition="center"
              type="submit"
              variant="contained"
            >
              {t("auth.forgotPassword.submitButton")}
            </Button>
          </Box>
        </form>
        <Divider sx={{ margin: "8px 0px" }} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            justifyContent: "space-between",
          }}
        >
          <RouteLink to="/users/sign_in">{t("common.signIn")}</RouteLink>
          <RouteLink to="/users/confirmation/new">
            {t("auth.signIn.resendConfirmationInstructionsLink")}
          </RouteLink>
        </Box>
      </AppCard>
    </Container>
  );
};

export const Route = createFileRoute("/_public/_auth/users/password/new")({
  component: RouteComponent,
});
